import type { AnalyticsReport } from "@server/types/analyticsReportTypes";
import { ArrowUpRight } from "lucide-react";
import { Fragment, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAnalyticsHistory } from "../hooks/useAnalyticsHistory";
import type { AnalyticsReportParams } from "../hooks/useAnalyticsReport";
import {
  formatAudioHours,
  formatDuration,
  formatErrorRate,
  formatRequests,
  formatXAxisLabel,
} from "../utils/formatMetric";
import { RequestExplorer } from "./RequestExplorer";
import { StreamingTranscriptionCard } from "./StreamingTranscriptionCard";
import "./endpoint-cards.css";
import {
  endpointSeries,
  endpointIdentity as identity,
  endpointProfile as profile,
} from "../utils/endpointMetrics";

type Metric =
  | "requests"
  | "audio"
  | "latency"
  | "errors"
  | "inputTokens"
  | "outputTokens";
type Endpoint = AnalyticsReport["endpoints"][number];
const labels: Record<Metric, string> = {
  requests: "Requests",
  audio: "Audio submitted",
  inputTokens: "Input tokens",
  outputTokens: "Output tokens",
  latency: "Response time",
  errors: "HTTP error rate",
};
const chartConfig = {
  inputTokens: { label: "Input tokens", color: "var(--chart-1)" },
  outputTokens: { label: "Output tokens", color: "var(--chart-1)" },
  requests: { label: "Requests", color: "var(--chart-1)" },
  audioSeconds: { label: "Audio submitted", color: "var(--chart-1)" },
  errorRate: { label: "HTTP error rate", color: "var(--chart-5)" },
  p50LatencyMs: { label: "P50", color: "var(--chart-1)" },
  p95LatencyMs: { label: "P95", color: "var(--chart-4)" },
};

function metrics(endpoint: Endpoint) {
  const latency = endpoint.latencySamples
    ? formatDuration({ ms: endpoint.p95LatencyMs })
    : "—";
  const data = [
    { label: "Requests", value: formatRequests({ n: endpoint.requests }) },
  ];
  if (endpoint.endpoint === "transcriptions" && endpoint.method === "POST")
    data.push({
      label: "Audio submitted",
      value: formatAudioHours({ seconds: endpoint.audioSecondsProcessed }),
    });
  else
    data.push({
      label:
        endpoint.endpoint === "notes" ? "P95 HTTP response" : "P95 response",
      value: latency,
    });
  data.push({
    label: "HTTP error rate",
    value: formatErrorRate({ rate: endpoint.errorRate }),
  });
  return data;
}
function choices(endpoint: Endpoint): Metric[] {
  return [
    "requests",
    ...(endpoint.endpoint === "transcriptions" && endpoint.method === "POST"
      ? ["audio" as Metric]
      : []),
    "latency",
    "errors",
    ...(endpoint.endpoint === "sully-m1"
      ? ["inputTokens" as Metric, "outputTokens" as Metric]
      : []),
  ];
}
function Trend({
  endpoint,
  timeseries,
  metric,
  params,
  compact = false,
}: {
  endpoint: Endpoint;
  timeseries: AnalyticsReport["timeseries"];
  metric: Metric;
  params: AnalyticsReportParams;
  compact?: boolean;
}) {
  const points = endpointSeries(endpoint, timeseries).map((b) => ({
    ...b,
    errorRate: b.requests ? (b.errors / b.requests) * 100 : null,
  }));
  const format = (value: number) =>
    metric === "audio"
      ? formatAudioHours({ seconds: value })
      : metric === "latency"
        ? formatDuration({ ms: value })
        : metric === "errors"
          ? `${value.toFixed(1)}%`
          : formatRequests({ n: value });
  const tokenMetric = metric === "inputTokens" || metric === "outputTokens";
  const hasTokenUsage =
    tokenMetric && points.some((point) => point[metric] != null);
  const field = tokenMetric
    ? metric
    : metric === "requests"
      ? "requests"
      : metric === "audio"
        ? "audioSeconds"
        : "errorRate";
  return (
    <div className="endpoint-trend">
      <div
        className="analytics-legend endpoint-legend-slot"
        aria-hidden={metric !== "latency"}
        style={{ visibility: metric === "latency" ? "visible" : "hidden" }}
      >
        <span>
          <i style={{ background: "var(--chart-1)" }} />
          P50
        </span>
        <span>
          <i style={{ background: "var(--chart-4)" }} />
          P95
        </span>
      </div>
      {tokenMetric && !hasTokenUsage && endpoint.requests > 0 ? (
        <div
          className="flex items-center justify-center text-xs text-muted-foreground"
          style={{ height: compact ? 145 : 270 }}
          role="status"
        >
          No {labels[metric].toLowerCase()} recorded for this period.
        </div>
      ) : (
        <ChartContainer
          key={metric}
          config={chartConfig}
          className={
            compact
              ? "h-[145px] w-full aspect-auto"
              : "h-[270px] w-full aspect-auto"
          }
        >
          <ComposedChart
            data={points}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            accessibilityLayer
            barCategoryGap="10%"
            barGap={1}
          >
            <CartesianGrid
              vertical={false}
              stroke="var(--border)"
              strokeDasharray="3 5"
            />
            <XAxis
              dataKey="timestamp"
              tickLine={false}
              axisLine={false}
              minTickGap={compact ? 64 : 48}
              tickFormatter={(timestamp) =>
                formatXAxisLabel({ timestamp, granularity: params.granularity })
              }
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={70}
              tickFormatter={format}
              allowDecimals={metric !== "requests" && !tokenMetric}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(v) =>
                    new Date(String(v)).toLocaleString("en-US", {
                      timeZone: "UTC",
                      month: "short",
                      day: "numeric",
                      ...(params.granularity === "minute" ||
                      params.granularity === "hour"
                        ? { hour: "numeric", minute: "2-digit" }
                        : {}),
                    })
                  }
                  formatter={(v, name, item) => (
                    <div className="endpoint-tooltip">
                      <span>
                        {chartConfig[name as keyof typeof chartConfig]?.label}
                      </span>
                      <strong>
                        {tokenMetric
                          ? Number(v).toLocaleString("en-US")
                          : format(Number(v))}
                      </strong>
                      {tokenMetric && (
                        <small className="text-muted-foreground">
                          Usage recorded for{" "}
                          {item.payload[
                            metric === "inputTokens"
                              ? "inputTokenRequests"
                              : "outputTokenRequests"
                          ] ?? 0}{" "}
                          of {item.payload.requests} requests
                        </small>
                      )}
                    </div>
                  )}
                />
              }
            />
            {metric === "latency" && (
              <Line
                dataKey="p50LatencyMs"
                stroke="var(--chart-1)"
                strokeWidth={2}
                connectNulls
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            )}
            {metric === "latency" && (
              <Line
                dataKey="p95LatencyMs"
                stroke="var(--chart-4)"
                strokeWidth={2}
                connectNulls
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            )}
            {metric === "errors" ? (
              <Bar
                dataKey={field}
                fill="var(--chart-5)"
                maxBarSize={24}
                radius={[2, 2, 0, 0]}
                isAnimationActive={false}
              />
            ) : (
              metric !== "latency" && (
                <Bar
                  dataKey={field}
                  fill="var(--chart-1)"
                  maxBarSize={compact ? 18 : 32}
                  radius={[2, 2, 0, 0]}
                  isAnimationActive={false}
                />
              )
            )}
          </ComposedChart>
        </ChartContainer>
      )}
    </div>
  );
}
function MetricTabs({
  endpoint,
  metric,
  setMetric,
}: {
  endpoint: Endpoint;
  metric: Metric;
  setMetric: (m: Metric) => void;
}) {
  return (
    <fieldset
      className="endpoint-metric-tabs"
      aria-label={`Metrics for ${profile(endpoint).name}`}
    >
      {choices(endpoint).map((m) => (
        <button
          key={m}
          aria-pressed={metric === m}
          onClick={() => setMetric(m)}
        >
          {labels[m]}
        </button>
      ))}
    </fieldset>
  );
}
function EndpointDetail({
  endpoint,
  params,
  timeseries,
}: {
  endpoint: Endpoint;
  params: AnalyticsReportParams;
  timeseries: AnalyticsReport["timeseries"];
}) {
  const [metric, setMetric] = useState<Metric>(
    choices(endpoint).includes(profile(endpoint).defaultMetric)
      ? profile(endpoint).defaultMetric
      : "requests",
  );
  const [cursors, setCursors] = useState<string[]>([""]);
  const [requestId, setRequestId] = useState("");
  const query = useAnalyticsHistory({
    from: params.from,
    to: params.to,
    apiKeyId: params.apiKeyId,
    status: params.status,
    requestType: identity(endpoint),
    requestId,
    cursor: cursors.at(-1) || undefined,
  });
  return (
    <div className="endpoint-detail">
      {query.isError && (
        <Alert variant="destructive">
          <AlertDescription>
            {query.data
              ? "Unable to update activity. Showing previously loaded activity. "
              : "Unable to load request history. "}
            <Button variant="link" onClick={() => query.refetch()}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <div className="endpoint-summary">
        {metrics(endpoint).map((m) => (
          <div key={m.label}>
            <span>{m.label}</span>
            <strong>{m.value}</strong>
          </div>
        ))}
      </div>
      <div className="endpoint-focus-chart">
        <MetricTabs endpoint={endpoint} metric={metric} setMetric={setMetric} />
        <Trend
          endpoint={endpoint}
          timeseries={timeseries}
          metric={metric}
          params={params}
        />
      </div>
      <RequestExplorer
        history={query.data}
        isFetching={query.isFetching}
        isError={query.isError}
        page={cursors.length}
        requestId={requestId}
        onSearch={(value) => {
          setRequestId(value);
          setCursors([""]);
        }}
        onPage={(direction) => {
          if (direction === "previous")
            setCursors((current) => current.slice(0, -1));
          else if (query.data?.nextCursor) {
            const next = query.data.nextCursor;
            setCursors((current) => [...current, next]);
          }
        }}
      />
    </div>
  );
}
function EndpointCard({
  endpoint,
  params,
  timeseries,
  onOpen,
}: {
  endpoint: Endpoint;
  params: AnalyticsReportParams;
  timeseries: AnalyticsReport["timeseries"];
  onOpen: () => void;
}) {
  const [metric, setMetric] = useState<Metric>(
    choices(endpoint).includes(profile(endpoint).defaultMetric)
      ? profile(endpoint).defaultMetric
      : "requests",
  );

  return (
    <article className="endpoint-card" aria-label={profile(endpoint).name}>
      <button
        className="endpoint-card-title"
        onClick={onOpen}
        aria-label={`Explore ${profile(endpoint).name} ${endpoint.method}`}
      >
        <span>
          {profile(endpoint).name}
          <small>
            {endpoint.endpoint === "sully-m1"
              ? "Chat Completions · Responses"
              : `${endpoint.method} · ${endpoint.endpoint}`}
          </small>
        </span>
        <ArrowUpRight size={17} />
      </button>
      <div className="endpoint-card-metrics">
        {metrics(endpoint).map((m) => (
          <span key={m.label}>
            <strong>{m.value}</strong>
            <small>{m.label}</small>
          </span>
        ))}
      </div>
      <MetricTabs endpoint={endpoint} metric={metric} setMetric={setMetric} />
      {endpoint.requests === 0 || endpoint.timeseries ? (
        <Trend
          endpoint={endpoint}
          timeseries={timeseries}
          metric={metric}
          params={params}
          compact
        />
      ) : (
        <div className="endpoint-loading">Endpoint chart data unavailable.</div>
      )}
    </article>
  );
}
/** Product cards remain visible without calls; charts share the main report and history loads only when opened. */
export function EndpointCards({
  report,
  params,
}: {
  report: AnalyticsReport;
  params: AnalyticsReportParams;
}) {
  const endpoints = report.endpoints.filter((e) => e.endpoint !== "models");
  for (const endpoint of ["notes", "transcriptions", "sully-m1", "utils"]) {
    if (!endpoints.some((e) => e.endpoint === endpoint && e.method === "POST"))
      endpoints.push({
        endpoint,
        method: "POST",
        requests: 0,
        errors: 0,
        clientErrors: 0,
        serverErrors: 0,
        errorRate: 0,
        avgLatencyMs: 0,
        p95LatencyMs: 0,
        audioSecondsProcessed: 0,
        latencySamples: 0,
        timeseries: [],
      });
  }
  endpoints.sort(
    (a, b) =>
      profile(a).order - profile(b).order ||
      identity(a).localeCompare(identity(b)),
  );
  const [opened, setOpened] = useState<string | null>(null);
  const endpoint = endpoints.find((e) => identity(e) === opened);
  return (
    <>
      <div className="endpoint-cards">
        {endpoints.map((e) =>
          e.endpoint === "transcriptions" && e.method === "POST" ? (
            <Fragment key={identity(e)}>
              <EndpointCard
                endpoint={e}
                params={params}
                timeseries={report.timeseries}
                onOpen={() => setOpened(identity(e))}
              />
              <StreamingTranscriptionCard />
            </Fragment>
          ) : (
            <EndpointCard
              key={identity(e)}
              endpoint={e}
              params={params}
              timeseries={report.timeseries}
              onOpen={() => setOpened(identity(e))}
            />
          ),
        )}
      </div>
      <Dialog
        open={Boolean(endpoint)}
        onOpenChange={(open) => {
          if (!open) setOpened(null);
        }}
      >
        <DialogContent className="endpoint-detail-dialog">
          {endpoint && (
            <>
              <DialogTitle>{profile(endpoint).name}</DialogTitle>
              <DialogDescription className="endpoint-route">
                {endpoint.endpoint === "sully-m1"
                  ? "Chat Completions · Responses"
                  : identity(endpoint)}
              </DialogDescription>
              <EndpointDetail
                key={`${identity(endpoint)}-${params.from}-${params.to}-${params.apiKeyId}-${params.status}-${params.granularity}`}
                endpoint={endpoint}
                params={params}
                timeseries={report.timeseries}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
